import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Boxes,
  CheckCircle2,
  Code2,
  ExternalLink,
  GitBranch,
  GitCommit,
  MonitorPlay,
  RefreshCw,
  Rocket,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { CapabilityStatus } from "../components/CapabilityStatus";
import BuildLincBaselineAdoption from "../components/BuildLincBaselineAdoption";
import BuildLincHistory from "../components/BuildLincHistory";
import BuildLincJobs from "../components/BuildLincJobs";
import "./BuildLincPage.css";

const COMMANDLINC_BOOTSTRAP_SHA = "27c56efe672440cd302fd621247cfb499651144f";
const PROJECT_STORAGE = "buildlinc.selectedProjectId";

function projectKey(project) {
  return project?.key || project?.slug || project?.id || "project";
}
function candidateStorage(project) {
  return `buildlinc.${projectKey(project)}.candidateBranch`;
}
function legacyBaselineStorage(project) {
  return `buildlinc.${projectKey(project)}.deployedSha`;
}
function shortSha(value) {
  return value ? String(value).slice(0, 10) : "—";
}
function apiMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return error?.message || fallback;
}
function CheckIcon({ state }) {
  if (state === "passed") return <CheckCircle2 size={15} />;
  if (state === "failed") return <XCircle size={15} />;
  return <RefreshCw size={15} />;
}

export default function BuildLincPage() {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(() => localStorage.getItem(PROJECT_STORAGE) || "");
  const [githubStatus, setGithubStatus] = useState(null);
  const [repo, setRepo] = useState(null);
  const [branches, setBranches] = useState([]);
  const [candidate, setCandidate] = useState("");
  const [deployment, setDeployment] = useState(null);
  const [releaseCandidate, setReleaseCandidate] = useState(null);
  const [previewEvidence, setPreviewEvidence] = useState(null);
  const [comparisonBaseline, setComparisonBaseline] = useState("");
  const [comparison, setComparison] = useState(null);
  const [checks, setChecks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [previewSaving, setPreviewSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewStatus, setPreviewStatus] = useState("passed");
  const [previewNote, setPreviewNote] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [analysisNotice, setAnalysisNotice] = useState(null);
  const handleJobError = useCallback((jobError, fallback) => setError(apiMessage(jobError, fallback)), []);

  const project = useMemo(() => projects.find((item) => item.id === selectedProjectId) || null, [projects, selectedProjectId]);
  const configured = githubStatus?.integration_status === "configured";
  const candidateBranch = useMemo(() => branches.find((branch) => branch.name === candidate), [branches, candidate]);
  const candidateSha = candidateBranch?.commit_sha || comparison?.head_sha || null;
  const deployedSha = deployment?.sha || comparisonBaseline || "";
  const currentRc = releaseCandidate?.sha && candidateSha && releaseCandidate.sha === candidateSha ? releaseCandidate : null;
  const currentPreview = previewEvidence?.sha && candidateSha && previewEvidence.sha === candidateSha ? previewEvidence : null;
  const previewPassed = currentPreview?.smoke_status === "passed";
  const historyRefreshToken = `${releaseCandidate?.ledger_event_id || ""}:${previewEvidence?.ledger_event_id || ""}:${deployment?.ledger_event_id || ""}`;

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/projects");
      const sourceProjects = (data || []).filter((item) => item.github?.repo_url);
      setProjects(sourceProjects);
      const stored = localStorage.getItem(PROJECT_STORAGE);
      const command = sourceProjects.find((item) => projectKey(item) === "commandlinc");
      const nextId = sourceProjects.some((item) => item.id === stored) ? stored : command?.id || sourceProjects[0]?.id || "";
      setSelectedProjectId(nextId);
      if (nextId) localStorage.setItem(PROJECT_STORAGE, nextId);
    } catch (e) {
      setError(apiMessage(e, "BuildLinc could not load LincWerks projects."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const loadSource = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    setError(null);
    setAnalysisNotice(null);
    setGithubStatus(null);
    setRepo(null);
    setBranches([]);
    setComparison(null);
    setChecks(null);
    setPreviewEvidence(null);
    try {
      const [{ data: status }, deploymentResult, rcResult] = await Promise.all([
        api.get(`/projects/${project.id}/github/status`),
        api.get(`/projects/${project.id}/buildlinc/deployment`).catch(() => ({ data: { recorded: false, deployment: null } })),
        api.get(`/projects/${project.id}/buildlinc/release-candidate`).catch(() => ({ data: { recorded: false, release_candidate: null } })),
      ]);
      setGithubStatus(status);

      const serverDeployment = deploymentResult?.data?.deployment || null;
      setDeployment(serverDeployment);
      setReleaseCandidate(rcResult?.data?.release_candidate || null);
      const key = projectKey(project);
      const fallbackBaseline = localStorage.getItem(legacyBaselineStorage(project))
        || (key === "commandlinc" ? COMMANDLINC_BOOTSTRAP_SHA : "");
      setComparisonBaseline(serverDeployment?.sha || fallbackBaseline);

      if (status.integration_status !== "configured") return;

      const results = await Promise.allSettled([
        api.get(`/projects/${project.id}/github/repo`),
        api.get(`/projects/${project.id}/github/branches`),
      ]);
      const repoResult = results[0];
      const branchResult = results[1];
      if (repoResult.status === "fulfilled") setRepo(repoResult.value.data);
      if (branchResult.status === "fulfilled") setBranches(branchResult.value.data || []);

      if (repoResult.status === "rejected" || branchResult.status === "rejected") {
        setAnalysisNotice("GitHub source data is temporarily degraded. BuildLinc is still usable; refresh when the upstream connection recovers.");
      }

      const repoData = repoResult.status === "fulfilled" ? repoResult.value.data : null;
      const branchData = branchResult.status === "fulfilled" ? (branchResult.value.data || []) : [];
      const storedCandidate = localStorage.getItem(candidateStorage(project));
      const preferred = storedCandidate
        || (key === "commandlinc" && branchData.some((branch) => branch.name === "agent/buildlinc-v1.1") ? "agent/buildlinc-v1.1" : "");
      const nextCandidate = branchData.some((branch) => branch.name === preferred)
        ? preferred
        : branchData.find((branch) => branch.name.startsWith("agent/"))?.name
          || repoData?.default_branch
          || "main";
      setCandidate(nextCandidate);
      localStorage.setItem(candidateStorage(project), nextCandidate);
    } catch (e) {
      setError(apiMessage(e, "BuildLinc could not load source state."));
    } finally {
      setLoading(false);
    }
  }, [project]);

  useEffect(() => { loadSource(); }, [loadSource]);

  const loadAnalysis = useCallback(async () => {
    if (!project?.id || !configured || !candidate) return;
    setAnalysisLoading(true);
    setAnalysisNotice(null);
    const checksRef = candidateBranch?.commit_sha || candidate;
    const requests = [
      deployedSha
        ? api.get(`/projects/${project.id}/buildlinc/compare?base=${encodeURIComponent(deployedSha)}&head=${encodeURIComponent(candidate)}`)
        : Promise.resolve({ data: null }),
      api.get(`/projects/${project.id}/buildlinc/checks?ref=${encodeURIComponent(checksRef)}`),
      api.get(`/projects/${project.id}/buildlinc/preview?sha=${encodeURIComponent(checksRef)}`),
    ];
    const [compareResult, checkResult, previewResult] = await Promise.allSettled(requests);

    const notices = [];
    if (compareResult.status === "fulfilled") setComparison(compareResult.value.data);
    else {
      setComparison(null);
      notices.push("comparison");
    }
    if (checkResult.status === "fulfilled") setChecks(checkResult.value.data);
    else {
      setChecks(null);
      notices.push("check status");
    }
    if (previewResult.status === "fulfilled") {
      const nextPreview = previewResult.value.data?.preview || null;
      setPreviewEvidence(nextPreview);
      if (nextPreview?.preview_url) setPreviewUrl(nextPreview.preview_url);
    }
    if (notices.length) {
      setAnalysisNotice(`GitHub ${notices.join(" and ")} temporarily unavailable. This is an upstream data issue, not a BuildLinc page failure.`);
    }
    setAnalysisLoading(false);
  }, [project?.id, configured, candidate, candidateBranch?.commit_sha, deployedSha]);

  useEffect(() => { loadAnalysis(); }, [loadAnalysis]);

  const selectProject = (value) => {
    setSelectedProjectId(value);
    localStorage.setItem(PROJECT_STORAGE, value);
  };

  const changeCandidate = (value) => {
    setCandidate(value);
    setCopied(false);
    setPreviewEvidence(null);
    setPreviewUrl("");
    if (project) localStorage.setItem(candidateStorage(project), value);
  };

  const prepareReleaseCandidate = async () => {
    if (!candidateSha || !project?.id || !deployedSha) return;
    if (!window.confirm("Prepare this exact green candidate as the release candidate for a milestone preview?")) return;
    setPreparing(true);
    setError(null);
    try {
      const { data } = await api.post(`/projects/${project.id}/buildlinc/release-candidate`, {
        sha: candidateSha,
        branch: candidate,
        base_sha: deployedSha,
        note: "Milestone preview candidate approved in BuildLinc",
      });
      setReleaseCandidate(data?.release_candidate || null);
      setCopied(false);
    } catch (e) {
      setError(apiMessage(e, "Could not prepare the release candidate."));
    } finally {
      setPreparing(false);
    }
  };

  const copyDeployHandoff = async () => {
    if (!currentRc?.deploy_handoff) return;
    try {
      await navigator.clipboard.writeText(currentRc.deploy_handoff);
      setCopied(true);
    } catch {
      setCopied(false);
      setError("Browser clipboard access was unavailable. Select and copy the deploy handoff text manually.");
    }
  };

  const recordPreview = async () => {
    if (!candidateSha || !project?.id || !currentRc || !previewUrl.trim()) return;
    setPreviewSaving(true);
    setError(null);
    try {
      const { data } = await api.post(`/projects/${project.id}/buildlinc/preview`, {
        sha: candidateSha,
        preview_url: previewUrl.trim(),
        smoke_status: previewStatus,
        note: previewNote.trim() || "External milestone preview result recorded in BuildLinc",
      });
      setPreviewEvidence(data?.preview || null);
    } catch (e) {
      setError(apiMessage(e, "Could not record preview evidence."));
    } finally {
      setPreviewSaving(false);
    }
  };

  const recordCandidateAsDeployed = async () => {
    if (!candidateSha || !project?.id || !previewPassed) return;
    if (!window.confirm("Record this exact preview-confirmed candidate SHA as deployed? Only confirm after the external deployment has succeeded.")) return;
    setRecording(true);
    setError(null);
    try {
      const { data } = await api.post(`/projects/${project.id}/buildlinc/deployment`, {
        sha: candidateSha,
        branch: candidate,
        preview_url: currentPreview?.preview_url || null,
        note: "External deployment confirmed after BuildLinc release-candidate preview",
      });
      const next = data?.deployment || null;
      setDeployment(next);
      setComparisonBaseline(next?.sha || candidateSha);
      localStorage.setItem(legacyBaselineStorage(project), next?.sha || candidateSha);
    } catch (e) {
      setError(apiMessage(e, "Could not record the deployment baseline."));
    } finally {
      setRecording(false);
    }
  };

  const releaseReady = Boolean(
    comparison
    && checks?.all_passed
    && comparison.behind_by === 0
    && candidateSha
    && deployedSha
    && candidateSha !== deployedSha
  );

  const ciState = checks?.state || "no_checks";
  const ciStatus = ciState === "passed" ? "live" : "in_development";
  const baselineSource = deployment?.sha ? "server + audit ledger" : comparisonBaseline ? "bootstrap/local fallback" : "not recorded";

  if (loading && !projects.length) {
    return <div className="buildlinc-page"><div className="buildlinc-loading"><RefreshCw size={16} /> Loading BuildLinc source state…</div></div>;
  }

  return (
    <div className="buildlinc-page" data-testid="buildlinc-page" data-edit-name="BuildLinc">
      <header className="buildlinc-hero" data-edit-name="BuildLinc overview">
        <div>
          <small>COMMANDLINC · BUILDLINC</small>
          <h1>Build here. Pay for preview only when it matters.</h1>
          <p>BuildLinc turns GitHub source, automated checks, deployed-vs-candidate comparison and release readiness into one development cockpit.</p>
        </div>
        <div className="buildlinc-hero-actions">
          <CapabilityStatus status="in_development" />
          <button className="btn small" onClick={() => { loadProjects(); loadSource(); loadAnalysis(); }} disabled={analysisLoading}><RefreshCw size={13} /> Refresh</button>
        </div>
      </header>

      <section className="buildlinc-project-bar" data-edit-name="BuildLinc project">
        <label><small>Project</small><select value={selectedProjectId} onChange={(e) => selectProject(e.target.value)}>{projects.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <div><small>Repository</small><b>{project?.github?.repo_url ? project.github.repo_url.replace("https://github.com/", "") : "No repository"}</b></div>
        <div><small>Deployment baseline</small><b className="mono">{shortSha(deployedSha)}</b><span>{baselineSource}</span></div>
      </section>

      {error ? <div className="buildlinc-alert"><AlertTriangle size={16} /><span>{error}</span></div> : null}
      {analysisNotice ? <div className="buildlinc-notice"><AlertTriangle size={15} /><span>{analysisNotice}</span></div> : null}

      {!project ? (
        <section className="buildlinc-connect"><GitBranch size={24} /><div><h2>No GitHub-backed project found</h2><p>Add a repository to a LincWerks project before using BuildLinc.</p></div></section>
      ) : !configured ? (
        <section className="buildlinc-connect">
          <GitBranch size={24} />
          <div><h2>GitHub connection required for {project.name}</h2><p>BuildLinc reuses the project's existing secure GitHub integration. Configure access once; no second BuildLinc credential is stored.</p></div>
          <Link className="btn primary" to="/commandlinc/integrations">Open integrations</Link>
        </section>
      ) : (
        <>
          {!deployedSha ? <BuildLincBaselineAdoption project={project} branches={branches} onAdopted={loadSource} /> : null}

          <section className="buildlinc-kpis" data-edit-name="BuildLinc checks">
            <article><small>Repository</small><strong>{repo?.full_name || project.name}</strong><span><CapabilityStatus status="live" compact /> source of truth</span></article>
            <article><small>Candidate</small><strong>{candidate || "—"}</strong><span className="mono">{shortSha(candidateSha)}</span></article>
            <article><small>Delta</small><strong>{comparison ? `+${comparison.ahead_by || 0}` : "—"}</strong><span>{comparison ? `${comparison.files_changed || 0} files changed` : deployedSha ? "waiting for compare" : "record deployment first"}</span></article>
            <article className={`ci-${ciState}`}><small>Automated checks</small><strong>{ciState.replace(/_/g, " ")}</strong><span><CapabilityStatus status={ciStatus} compact /> {checks?.check_run_count || 0} check runs</span></article>
          </section>

          <section className="buildlinc-workspace">
            <BuildLincJobs projectId={project.id} branch={candidate} sha={candidateSha} onError={handleJobError} />
            <article className="buildlinc-panel buildlinc-source-panel">
              <header><div><GitBranch size={16} /><span><b>Candidate vs deployed</b><small>Choose what we are building and compare it to the confirmed release.</small></span></div><CapabilityStatus status={deployment?.sha ? "live" : "workflow_ready"} compact /></header>
              <div className="buildlinc-form-grid">
                <label><small>Candidate branch</small><select value={candidate} onChange={(e) => changeCandidate(e.target.value)}>{branches.map((branch) => <option key={branch.name} value={branch.name}>{branch.name}</option>)}</select></label>
                <label><small>Confirmed deployed SHA</small><div className="buildlinc-readonly"><b className="mono">{deployedSha || "Not recorded"}</b><span>{baselineSource}</span></div></label>
              </div>
              <div className="buildlinc-compare-strip">
                <div><small>Deployed</small><b className="mono">{shortSha(deployedSha)}</b></div><ArrowRight size={18} /><div><small>Candidate</small><b className="mono">{shortSha(candidateSha)}</b></div>
              </div>
              {comparison ? <div className="buildlinc-delta-grid"><span><small>Ahead</small><b>{comparison.ahead_by || 0}</b></span><span><small>Behind</small><b>{comparison.behind_by || 0}</b></span><span><small>Commits</small><b>{comparison.total_commits || 0}</b></span><span><small>Files</small><b>{comparison.files_changed || 0}</b></span></div> : <p className="buildlinc-hint">{deployedSha ? "Comparison is waiting on GitHub data." : "Record a confirmed deployment before using deployed-vs-candidate comparison."}</p>}
            </article>

            <article className="buildlinc-panel buildlinc-checks-panel">
              <header><div><Code2 size={16} /><span><b>Build + validation</b><small>Cheap checks run in GitHub before we spend on a preview.</small></span></div><CheckIcon state={ciState} /></header>
              <div className="buildlinc-check-list">
                {checks?.check_runs?.length ? checks.check_runs.map((run) => (
                  <a key={run.id || run.name} href={run.details_url || "#"} target={run.details_url ? "_blank" : undefined} rel="noreferrer">
                    <span><CheckIcon state={run.conclusion === "success" || run.conclusion === "neutral" || run.conclusion === "skipped" ? "passed" : run.status === "completed" ? "failed" : "running"} /><span><b>{run.name}</b><small>{run.app || "GitHub"} · {run.status}{run.conclusion ? ` · ${run.conclusion}` : ""}</small></span></span>{run.details_url ? <ExternalLink size={12} /> : null}
                  </a>
                )) : <p>No GitHub check runs are visible for this candidate yet.</p>}
              </div>
              <div className="buildlinc-scope-note"><ShieldCheck size={14} /><span><b>V1 test boundary</b><small>Frontend production compile + backend static validation are automatic for CommandLinc. Full authenticated runtime/AI integration QA remains a milestone check.</small></span></div>
            </article>

            <article className="buildlinc-panel buildlinc-files-panel">
              <header><div><Boxes size={16} /><span><b>Changed surface</b><small>What differs from the confirmed deployed baseline.</small></span></div><em>{comparison?.files_changed || 0}</em></header>
              <div className="buildlinc-file-list">
                {comparison?.files?.length ? comparison.files.slice(0, 12).map((file) => <div key={file.filename}><span><b>{file.filename}</b><small>{file.status}</small></span><em>+{file.additions} / -{file.deletions}</em></div>) : <p>No changed-file comparison available yet.</p>}
              </div>
            </article>

            <article className="buildlinc-panel buildlinc-release-panel">
              <header><div><Rocket size={16} /><span><b>Milestone preview + release</b><small>External runtime becomes the exception, not the development loop.</small></span></div><CapabilityStatus status={previewPassed ? "workflow_ready" : releaseReady ? "workflow_ready" : "in_development"} compact /></header>
              <div className="buildlinc-release-steps">
                <div className={checks?.all_passed ? "done" : ""}><CheckCircle2 size={15} /><span><b>Automated checks</b><small>{checks?.all_passed ? "Green" : "Waiting for green CI"}</small></span></div>
                <div className={comparison && comparison.behind_by === 0 ? "done" : ""}><GitCommit size={15} /><span><b>Candidate lineage</b><small>{comparison ? `${comparison.ahead_by || 0} ahead / ${comparison.behind_by || 0} behind deployed` : "Waiting for comparison"}</small></span></div>
                <div className={currentRc ? "done" : ""}><Rocket size={15} /><span><b>Release candidate</b><small>{currentRc ? `Prepared ${shortSha(currentRc.sha)}` : "Human approval not recorded yet"}</small></span></div>
                <div className={previewPassed ? "done" : ""}><MonitorPlay size={15} /><span><b>Full-stack preview</b><small>{currentPreview ? `${currentPreview.smoke_status} · ${currentPreview.preview_url}` : "Not connected internally; record the external milestone result here"}</small></span></div>
              </div>

              {!currentRc ? (
                <div className="buildlinc-release-banner">
                  <b>{releaseReady ? "Ready to prepare a release candidate" : "Keep building without deploying"}</b>
                  <p>{releaseReady ? "The candidate is ahead of the deployed baseline and automated checks are green. Prepare it only when the batch is worth a paid preview." : "BuildLinc has not yet reached the point where an external preview is worth the spend."}</p>
                  <button className="btn primary" disabled={!releaseReady || preparing} onClick={prepareReleaseCandidate}>{preparing ? "Verifying…" : "Prepare release candidate"}</button>
                </div>
              ) : (
                <div className="buildlinc-rc-box">
                  <div><small>RELEASE CANDIDATE</small><b className="mono">{currentRc.sha}</b><span>{currentRc.branch}</span></div>
                  <button className="btn small" onClick={copyDeployHandoff}>{copied ? "Copied" : "Copy deploy handoff"}</button>
                  <textarea readOnly value={currentRc.deploy_handoff || ""} aria-label="Deploy Only handoff" />
                </div>
              )}

              {currentRc ? (
                <div className="buildlinc-preview-form">
                  <label><small>Preview URL returned by runtime</small><input value={previewUrl} onChange={(e) => setPreviewUrl(e.target.value)} placeholder="https://..." /></label>
                  <label><small>Minimum smoke result</small><select value={previewStatus} onChange={(e) => setPreviewStatus(e.target.value)}><option value="passed">Passed</option><option value="partial">Partial</option><option value="failed">Failed</option></select></label>
                  <label className="wide"><small>Runtime note / meaningful delta</small><input value={previewNote} onChange={(e) => setPreviewNote(e.target.value)} placeholder="Optional concise note" /></label>
                  <button className="btn" disabled={!previewUrl.trim() || previewSaving} onClick={recordPreview}>{previewSaving ? "Recording…" : "Record preview result"}</button>
                </div>
              ) : null}

              <button className="btn" disabled={!previewPassed || recording} onClick={recordCandidateAsDeployed}>{recording ? "Recording…" : "Record confirmed deployment"}</button>
              <small className="buildlinc-warning">Director-only. Release candidate, preview result and confirmed deployment are separate human-gated audit events. BuildLinc never treats a preview as a production deployment automatically.</small>
            </article>
          </section>

          <BuildLincHistory projectId={project.id} refreshToken={historyRefreshToken} />
        </>
      )}
    </div>
  );
}
